import { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type {
  ReportScanArea,
  ReportScanComparison,
  ReportScanGrid,
  ReportScanMapView,
} from '../types/report.types';
import { formatMonthDay, formatWeekdayMonthDay } from '../reportDate';
import {
  ACCENT_PILL_BG,
  BODY_FG,
  CARD_BORDER,
  FAINT_FG,
  GRID_MAP_BG,
  GRID_MAP_IMAGE,
  GRID_MAP_SIZE,
  HEADING_FG,
  INNER_RULE,
  MUTED_FG,
  ON_DARK_FG,
  PIN_BORDER,
  PIN_RANKED,
  PIN_TOP10,
  PIN_TOP3,
  PIN_UNRANKED,
  POSITIVE_BG,
  POSITIVE_FG,
  RANK_OTHER_FG,
  ROW_HOVER_BG,
  ROW_SELECTED_BG,
  ROW_SELECTED_FG,
  STAT_CARD_BG,
  SURFACE,
  TOOLTIP_BG,
  WARN_BG,
  WARN_FG,
  WORK_POST_BG,
  WORK_POST_FG,
  WORK_RULE_STRONG,
} from '../reportTokens';
import { percentText } from '../reportPercent';

/**
 * The scan grid's centrepiece: baseline-vs-now pin grids for a tracked
 * keyword, four stat cards, and an extended keyword table. Everything here
 * reads real per-point ranks from `mapPack.scanGrid` (see report.types.ts) —
 * unlike the design file's own `makeGrid`, nothing here is derived from a
 * visibility percentage or a distance-falloff curve. `MapPackSection` in
 * `ReportSections.tsx` only renders this when `scanGrid` actually has rows;
 * a v2 payload (no `scanGrid`) keeps the older, simpler keyword table.
 */

/**
 * A pin's footprint, by how many fit across the square.
 *
 * The design draws a 5x5 at 38px a pin. The grid itself is fluid (one square
 * aspect ratio, `1fr` columns), so a larger grid has to put smaller pins in
 * the same box — a 15x15, which is the current default, would otherwise
 * overlap into an unreadable mass. 38px is the ceiling, so the design's own
 * size is drawn at exactly the design's size.
 */
const PIN_MAX = 38;
const pinSize = (dimension: number) =>
  Math.max(14, Math.min(PIN_MAX, Math.round(300 / Math.max(dimension, 1))));

/** 14px on the design's 38px pin, scaled with it and floored where digits
 * stop being legible at all. */
const pinFontSize = (pin: number) => Math.max(8, Math.round(pin * 0.37));

/** The narrowest cell, in px, that still shows the rank inside its pin — at
 * the 8px floor the font already had, so a large grid on a phone keeps its
 * numbers, and only a square shrunk onto a shared map loses them. */
const PIN_LABEL_MIN = 12;

/**
 * Where a point lands on a grid's static map, as fractions of the image's
 * side: Web Mercator in a 256px world at zoom 0, the projection Maps Static
 * draws with, scaled by the zoom and measured from the centre the image was
 * requested at. `size` is the image's side in the map's own pixels (`scale=2`
 * doubles the bitmap, not the ground it covers), so one formula holds for
 * the inline square, the enlarged one and a map both sides share.
 */
export function projectToMap(
  view: Pick<ReportScanMapView, 'lat' | 'lng' | 'zoom' | 'size'>,
  lat: number,
  lng: number,
): { x: number; y: number } {
  const worldX = (value: number) => ((value + 180) / 360) * 256;
  const worldY = (value: number) => {
    const sin = Math.min(
      Math.max(Math.sin((value * Math.PI) / 180), -0.9999),
      0.9999,
    );
    return (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * 256;
  };
  const scale = 2 ** view.zoom;
  return {
    x: 0.5 + ((worldX(lng) - worldX(view.lng)) * scale) / view.size,
    y: 0.5 + ((worldY(lat) - worldY(view.lat)) * scale) / view.size,
  };
}

/** Whether a grid is drawn as its scanned points rather than as cells: a
 * snapshot that stored them, over a map they can be projected onto. */
const drawsPins = (grid: ReportScanGrid | null | undefined) =>
  Boolean(grid?.pins?.length && grid.map);

/**
 * Every scanned point placed on its grid's map, and the room each has.
 *
 * `spacing` stands in for a cell's width, in fractions of the image: a
 * grid's own cell, or the distance from a pin to its nearest neighbour where
 * that is closer — a circle's rings set some pins nearer than the lattice
 * does. A classic scan has no cell, so its pins get the nearest-neighbour
 * distance alone; the median, so the odd pair set close together does not
 * shrink every pin.
 */
function pinLayout(grid: ReportScanGrid) {
  const view = grid.map as ReportScanMapView;
  const spots = (grid.pins ?? []).map(([lat, lng, rank], index) => ({
    ...projectToMap(view, lat, lng),
    rank,
    index,
  }));
  const nearest = spots
    .map(a =>
      Math.min(
        ...spots.map(b =>
          a === b ? Infinity : Math.hypot(a.x - b.x, a.y - b.y),
        ),
      ),
    )
    .filter(distance => Number.isFinite(distance) && distance > 0)
    .sort((a, b) => a - b);
  const median = nearest.length
    ? nearest[Math.floor(nearest.length / 2)]
    : null;
  const cell = view.box.w / Math.max(grid.dimension, 1);
  const spacing =
    median === null
      ? cell
      : grid.dimension > 0
        ? Math.min(cell, median)
        : median;
  // Pins across the square, for the size ceiling `pinSize` sets by it.
  const across =
    grid.dimension > 0
      ? grid.dimension
      : Math.max(1, Math.round(view.box.w / spacing));
  return { view, spots, spacing, across };
}

/** A pin's colour band and label, by real rank. `null` means the point did
 * not place in the scanned results at all — rendered as `20+`, never as a
 * number the point never held (not 0, not a stale rank). */
function pinStyle(rank: number | null): { bg: string; label: string } {
  if (rank === null) return { bg: PIN_UNRANKED, label: '20+' };
  if (rank <= 3) return { bg: PIN_TOP3, label: String(rank) };
  if (rank <= 10) return { bg: PIN_TOP10, label: String(rank) };
  return { bg: PIN_RANKED, label: String(rank) };
}

/**
 * The "Avg now" cell's band.
 *
 * Wider bands than `pinStyle` above, and deliberately so: a pin is one
 * point's exact position, while this is a mean over every point on the grid.
 * An average of 9.7 means the business is on page one across its whole
 * service area, which is the good outcome the design paints green — holding
 * a mean to the top-3 bar a single pin is judged against would paint almost
 * every real report red.
 */
function averageBand(rank: number | null): { fg: string; bg: string } {
  if (rank === null) return { fg: RANK_OTHER_FG, bg: SURFACE };
  if (rank <= 10) return { fg: POSITIVE_FG, bg: POSITIVE_BG };
  if (rank <= 15) return { fg: WARN_FG, bg: WARN_BG };
  return { fg: RANK_OTHER_FG, bg: SURFACE };
}

/**
 * An average position for the table.
 *
 * A null average is not missing data — it means the scan ran and found the
 * business at no point on the grid. The pins already say that as `20+`, and
 * the table says it the same way rather than with an em dash that reads as
 * "we did not look".
 */
const averageText = (rank: number | null) =>
  rank === null ? '20+' : rank.toFixed(1);

/**
 * `baselineDate` arrives as a `Date`, serialised over the wire as a full ISO
 * timestamp (`2026-08-01T00:00:00.000Z`), not a bare `yyyy-mm-dd`. The old
 * `iso.split('-').map(Number)` here read the time-and-zone tail
 * (`'00:00:00.000Z'`) as `NaN`, so this returned null on every real report and
 * the caption silently dropped the date. `formatMonthDay` (the shared
 * UTC-calendar-date helper every other date on this page now uses) parses
 * either shape correctly.
 */
const formatBaselineDate = formatMonthDay;

/**
 * What a classic scan is called on the page. A classic scan predates the
 * grid and recorded no size, so it has points but no square to lay them on
 * (`dimension` 0). Older snapshots kept no point count, and say less.
 */
const classicScanText = (grid: ReportScanGrid) => {
  const points = grid.points ?? 0;
  return points > 0
    ? `Classic scan · ${points} ${points === 1 ? 'point' : 'points'}`
    : 'Classic scan';
};

/** Stands in for a square that has no pins to draw, saying why. */
function SquarePlaceholder({ children }: { children: string }) {
  return (
    <Box
      sx={{
        aspectRatio: '1',
        borderRadius: '12px',
        border: `1px dashed ${CARD_BORDER}`,
        bgcolor: SURFACE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1,
      }}
    >
      <Typography
        sx={{ fontSize: '12px', color: MUTED_FG, textAlign: 'center' }}
      >
        {children}
      </Typography>
    </Box>
  );
}

/**
 * One ranked point. `cell` is the room it has, as a CSS length expression
 * over the container's `100cqw`: a cell of the square, or the spacing
 * between pins drawn at their coordinates. `at` places it by its centre,
 * as fractions of the container; without it the pin sits in its grid cell.
 */
function Pin({
  rank,
  name,
  pin,
  cell,
  chosen,
  onSelect,
  at,
}: {
  rank: number | null;
  /** Where the pin is, for assistive tech; the label is appended. */
  name: string;
  pin: number;
  cell: string;
  chosen: boolean;
  onSelect?: () => void;
  at?: { x: number; y: number };
}) {
  // Where two placed pins overlap, the better rank is drawn on top — a 12
  // over a point that also ranked 3 understates the map. Stacked by z-index
  // rather than by order, so tabbing still walks the pins in scan order.
  const layer = chosen ? 100 : rank === null ? 1 : Math.max(2, 99 - rank);
  const style = pinStyle(rank);
  const label = `${name}: ${style.label}`;
  return (
    <Box
      data-testid='scan-pin'
      {...(onSelect
        ? {
            component: 'button' as const,
            type: 'button' as const,
            onClick: onSelect,
            'aria-label': label,
            'aria-pressed': chosen,
          }
        : { role: 'img', 'aria-label': label, title: label })}
      sx={{
        // The design's size, or the cell's, whichever is smaller.
        // Height set the same way, not by aspect ratio: under the
        // `max-height` clamp below, an aspect ratio gives way and the
        // pin draws as an oval.
        width: `min(${pin}px, calc(${cell} * 0.92))`,
        height: `min(${pin}px, calc(${cell} * 0.92))`,
        ...(at
          ? {
              position: 'absolute',
              left: `${at.x * 100}%`,
              top: `${at.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: layer,
            }
          : { maxWidth: '100%', maxHeight: '100%' }),
        borderRadius: '50%',
        bgcolor: style.bg,
        color: ON_DARK_FG,
        // "20+" is a digit wider than any rank, and clips at the size
        // a two-digit rank fits.
        fontSize: `calc(clamp(8px, calc(${cell} * 0.5), ${pinFontSize(pin)}px) * ${style.label.length > 2 ? 0.8 : 1})`,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: chosen
          ? `0 0 0 3px ${HEADING_FG}`
          : '0 2px 6px rgba(15,15,26,0.35)',
        border: `2px solid ${PIN_BORDER}`,
        boxSizing: 'border-box',
        overflow: 'hidden',
        ...(onSelect
          ? {
              p: 0,
              // Family only: the `font` shorthand would reset the
              // size set above.
              fontFamily: 'inherit',
              cursor: 'pointer',
              '&:focus-visible': {
                outline: `2px solid ${HEADING_FG}`,
                outlineOffset: '1px',
              },
            }
          : {}),
      }}
    >
      <span data-pin-label>{style.label}</span>
    </Box>
  );
}

/**
 * Every scanned point, drawn where it was scanned.
 *
 * Over the map each pin is projected onto the image (`projectToMap`), the
 * same square the image fills. Over the drawn stand-in there is no street
 * to match, so the scan's square (`box`) is stretched to fill the frame
 * inside the same margin the cell layout keeps — the shape the scan made,
 * at the size the frame allows.
 */
function PinsByCoordinates({
  layout,
  onMap,
  pin,
  selectedCell,
  onSelectCell,
}: {
  layout: ReturnType<typeof pinLayout>;
  onMap: boolean;
  pin: number;
  selectedCell?: number | null;
  onSelectCell?: (index: number) => void;
}) {
  const { box } = layout.view;
  const place = (spot: { x: number; y: number }) =>
    onMap ? spot : { x: (spot.x - box.x) / box.w, y: (spot.y - box.y) / box.h };
  // The room each pin has, as a share of the layer's width.
  const room = onMap ? layout.spacing : layout.spacing / box.w;
  return (
    <Box
      data-testid='scan-grid-pins'
      sx={{
        position: 'absolute',
        ...(onMap ? { inset: 0 } : { inset: '8px' }),
        // Pins stack by rank; this keeps that stacking inside the layer, so
        // it never reaches past the square or over the enlarge button.
        isolation: 'isolate',
        // A container, so a pin can size itself to its spacing: on a shared
        // map the smaller scan can be a fraction of the frame.
        containerType: 'inline-size',
        // Below this a pin cannot hold a legible number, so it is its colour
        // alone — the band still reads, and the rank is on the pin's title.
        [`@container (max-width: ${Math.round(PIN_LABEL_MIN / room)}px)`]: {
          '& [data-pin-label]': { display: 'none' },
        },
      }}
    >
      {layout.spots.map(spot => (
        <Pin
          key={spot.index}
          rank={spot.rank}
          // A scan has no rows to name a point by; its place in the scan is
          // what tells two pins apart.
          name={`Point ${spot.index + 1}`}
          pin={pin}
          cell={`(100cqw * ${room.toFixed(5)})`}
          chosen={selectedCell === spot.index}
          onSelect={onSelectCell && (() => onSelectCell(spot.index))}
          at={place(spot)}
        />
      ))}
    </Box>
  );
}

export type ScanMapSide = 'now' | 'baseline';
/** A grid's street map image, by keyword position and side. */
export type ScanMapUrl = (index: number, side: ScanMapSide) => string;

/** One square of pins, or — when `grid` is null — a same-sized placeholder
 * saying there is nothing to compare against yet. `baseline` is null when a
 * keyword has no earlier scan; that is a real state, not a bug, so it never
 * falls back to rendering the "now" grid twice. A grid with `pins` draws each
 * scanned point where it was scanned; one without (a snapshot stored before
 * they were) draws its cells. A classic scan has no square of cells
 * (dimension 0), so without pins it gets a placeholder of its own, rather
 * than an empty map that reads as a scan that found nothing. */
function ScanGridSquare({
  grid,
  fallbackDimension = 0,
  map,
  onMapFailed,
  onOpen,
  openLabel,
  selectedCell,
  onSelectCell,
  pinScale = 1,
}: {
  grid: ReportScanGrid | null;
  /** The placeholder's size when `grid` is null. */
  fallbackDimension?: number;
  /** The street map to draw under the pins, or null for the stand-in. Decided
   * by `ScanGridSection`, which also decides whether the pair is to scale —
   * one place, so the two can never disagree. */
  map?: ScanMap | null;
  /** Told when the map would not load. */
  onMapFailed?: (src: string) => void;
  /** Lays a button over the square that opens the enlarged view. */
  onOpen?: () => void;
  openLabel?: string;
  /** Makes each pin a button, for the enlarged view's point details. */
  selectedCell?: number | null;
  onSelectCell?: (index: number) => void;
  /** Larger pins where the square is drawn larger. */
  pinScale?: number;
}) {
  const dimension = grid ? grid.dimension : fallbackDimension;
  // Placing up to 225 pins measures every pair, so it is done once per grid
  // rather than again on every tap in the enlarged view.
  const layout = useMemo(
    () => (grid && drawsPins(grid) ? pinLayout(grid) : null),
    [grid],
  );

  if (!grid) return <SquarePlaceholder>No earlier scan</SquarePlaceholder>;
  // A classic scan stored before its points were has nothing to place.
  if (grid.dimension === 0 && !layout) {
    return <SquarePlaceholder>{classicScanText(grid)}</SquarePlaceholder>;
  }

  const pin = Math.min(
    PIN_MAX,
    Math.round(pinSize(layout ? layout.across : dimension) * pinScale),
  );
  const view = map ? grid.map : null;
  const outside = new Set(grid.outsideCells ?? []);
  const gaps = new Set(grid.gapCells ?? []);

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: CARD_BORDER,
      }}
    >
      {/* The drawn stand-in — see `GRID_MAP_IMAGE`. Always laid down, so the
          square is never blank while the street map loads or if it fails.
          Its own layer so the pin grid above can keep `place-items: center`
          without inheriting a background-size that has nothing to do with
          the pin spacing. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: GRID_MAP_BG,
          backgroundImage: GRID_MAP_IMAGE,
          backgroundSize: GRID_MAP_SIZE,
        }}
      />
      {/* The ground the scan covered. Shown whole — never cropped or zoomed
          past its edges — because the corners carry Google's logo and map
          credit, which have to stay visible. */}
      {map && view && (
        <Box
          component='img'
          data-testid='scan-grid-map'
          src={map.src}
          alt=''
          aria-hidden
          onError={() => onMapFailed?.(map.src)}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      )}
      {layout ? (
        <PinsByCoordinates
          layout={layout}
          onMap={Boolean(view)}
          pin={pin}
          selectedCell={selectedCell}
          onSelectCell={onSelectCell}
        />
      ) : (
        <Box
          data-testid='scan-grid-pins'
          sx={{
            position: 'absolute',
            display: 'grid',
            gridTemplateColumns: `repeat(${dimension}, 1fr)`,
            gridTemplateRows: `repeat(${dimension}, 1fr)`,
            placeItems: 'center',
            // Over a map, the square sits exactly where the scan ran, so a pin
            // lands on the point it stands for. Over the stand-in it just fills
            // the frame.
            // A container, so a pin can size itself to its cell: on a shared map
            // the smaller grid's square can be a fraction of the frame.
            containerType: 'inline-size',
            ...(view
              ? {
                  left: `${view.box.x * 100}%`,
                  top: `${view.box.y * 100}%`,
                  width: `${view.box.w * 100}%`,
                  height: `${view.box.h * 100}%`,
                }
              : { inset: 0, p: 1 }),
            // Below this a cell cannot hold a legible number, so the pin is
            // its colour alone — the band still reads, and the rank is on the
            // pin's title.
            [`@container (max-width: ${PIN_LABEL_MIN * dimension}px)`]: {
              '& [data-pin-label]': { display: 'none' },
            },
          }}
        >
          {grid.cells.map((rank, index) => {
            // Never searched, so nothing to draw: the corners around a circle.
            if (outside.has(index)) return <Box key={index} aria-hidden />;
            // Never searched, inside the scanned area. A small dot keeps the
            // circle whole without claiming a rank.
            if (gaps.has(index)) {
              return (
                <Box
                  key={index}
                  title='Not scanned'
                  aria-hidden
                  sx={{
                    width: `min(8px, calc(100cqw / ${dimension} * 0.3))`,
                    height: `min(8px, calc(100cqw / ${dimension} * 0.3))`,
                    borderRadius: '50%',
                    bgcolor: PIN_UNRANKED,
                    opacity: 0.6,
                  }}
                />
              );
            }
            // Where the pin sits, not its place in the scan's order: a circle
            // is walked ring by ring, so no running number matches the cells.
            return (
              <Pin
                key={index}
                rank={rank}
                name={`Row ${Math.floor(index / dimension) + 1}, column ${(index % dimension) + 1}`}
                pin={pin}
                cell={`(100cqw / ${dimension})`}
                chosen={selectedCell === index}
                onSelect={onSelectCell && (() => onSelectCell(index))}
              />
            );
          })}
        </Box>
      )}
      {/* Laid over the square rather than wrapping it, so the pins stay
          readable to assistive tech and the markup stays valid — a button
          may not hold the square's block layers. */}
      {onOpen && (
        <Box
          component='button'
          type='button'
          onClick={onOpen}
          aria-label={openLabel}
          sx={{
            position: 'absolute',
            inset: 0,
            p: 0,
            m: 0,
            border: 0,
            bgcolor: 'transparent',
            cursor: 'zoom-in',
            borderRadius: '12px',
            '&:hover [data-enlarge], &:focus-visible [data-enlarge]': {
              opacity: 1,
            },
            '&:focus-visible': {
              outline: `2px solid ${WORK_POST_FG}`,
              outlineOffset: '-2px',
            },
          }}
        >
          {/* Top right, clear of the corners Google credits itself in. */}
          <Box
            component='span'
            aria-hidden
            data-enlarge
            className='report-no-print'
            sx={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              width: 26,
              height: 26,
              borderRadius: '8px',
              bgcolor: SURFACE,
              color: HEADING_FG,
              boxShadow: '0 1px 4px rgba(15,15,26,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.85,
            }}
          >
            <Box
              component='svg'
              viewBox='0 0 24 24'
              width={15}
              height={15}
              fill='none'
              stroke='currentColor'
              strokeWidth={2}
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M15 3h6v6' />
              <path d='M9 21H3v-6' />
              <path d='M21 3l-7 7' />
              <path d='M3 21l7-7' />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** The business's own rank at a tapped pin: a scanned point where the grid
 * draws them, a cell where it draws those. */
const rankAt = (grid: ReportScanGrid, index: number) =>
  drawsPins(grid) ? (grid.pins?.[index]?.[2] ?? null) : grid.cells[index];

/**
 * The top 3 where one pin was scanned, the business slotted in at its own
 * rank. Null where the snapshot kept no competitors for it.
 */
function pointLeaders(
  grid: ReportScanGrid,
  index: number,
): Array<{ rank: number; name: string; you: boolean }> | null {
  const held = (drawsPins(grid) ? grid.pinLeaders : grid.cellLeaders)?.[index];
  const names = grid.competitorNames;
  if (!held || !names) return null;
  const rows = held
    .map(([name, rank]) => ({ rank, name: names[name] ?? '', you: false }))
    .filter(row => row.name);
  const own = rankAt(grid, index);
  if (own !== null && own <= 3)
    rows.push({ rank: own, name: 'You', you: true });
  return rows.sort((a, b) => a.rank - b.rank).slice(0, 3);
}

/** Whether a grid has pins to draw: a square, or a classic scan whose
 * points the snapshot stored. */
const canEnlarge = (grid: ReportScanGrid | null): grid is ReportScanGrid =>
  Boolean(grid && (grid.dimension > 0 || drawsPins(grid)));

/** What one tapped pin says: the business's rank there, and who held the
 * top 3. */
function PointDetail({
  grid,
  index,
}: {
  grid: ReportScanGrid;
  index: number | null;
}) {
  if (index === null) {
    return (
      <Typography sx={{ fontSize: '13px', color: MUTED_FG }}>
        Tap a pin to see who ranks at that point.
      </Typography>
    );
  }
  const rank = rankAt(grid, index);
  const leaders = pointLeaders(grid, index);
  return (
    <Box data-testid='point-detail'>
      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: HEADING_FG }}>
        {rank === null
          ? "You're not in the top 20 here"
          : `You rank #${rank} here`}
      </Typography>
      {leaders && leaders.length > 0 && (
        <>
          <Typography
            sx={{ fontSize: '12px', color: MUTED_FG, mt: '8px', mb: '4px' }}
          >
            Top 3 at this point
          </Typography>
          <Box component='ol' sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {leaders.map(row => (
              <Box
                component='li'
                key={`${row.rank}-${row.name}`}
                sx={{
                  display: 'flex',
                  gap: '8px',
                  fontSize: '13px',
                  py: '2px',
                  color: row.you ? HEADING_FG : BODY_FG,
                  fontWeight: row.you ? 700 : 500,
                }}
              >
                <Box component='span' sx={{ color: MUTED_FG, minWidth: 18 }}>
                  {row.rank}.
                </Box>
                {row.name}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

/**
 * One side's grid drawn large, with every pin tappable. Inline the pins of a
 * 15x15 are a few pixels across on a phone — too small to aim at — so details
 * live here rather than on the page.
 */
function EnlargedScanMap({
  comparison,
  side,
  onSide,
  onClose,
  baselineMap,
  nowMap,
  onMapFailed,
}: {
  comparison: ReportScanComparison;
  side: ScanMapSide | null;
  onSide: (side: ScanMapSide) => void;
  onClose: () => void;
  baselineMap: ScanMap | null;
  nowMap: ScanMap | null;
  onMapFailed: (src: string) => void;
}) {
  const [cell, setCell] = useState<number | null>(null);
  const grid = side === 'baseline' ? comparison.baseline : comparison.now;
  const choose = (next: ScanMapSide) => {
    setCell(null);
    onSide(next);
  };
  const close = () => {
    setCell(null);
    onClose();
  };
  const tabSx = (on: boolean) => ({
    border: '1px solid',
    borderColor: on ? WORK_POST_FG : CARD_BORDER,
    bgcolor: on ? WORK_POST_BG : ACCENT_PILL_BG,
    color: on ? WORK_POST_FG : BODY_FG,
    fontSize: '12px',
    fontWeight: on ? 700 : 600,
    borderRadius: '9999px',
    padding: '5px 12px',
    cursor: 'pointer',
  });

  return (
    <Dialog
      open={side !== null && grid !== null}
      onClose={close}
      fullWidth
      maxWidth='sm'
      aria-labelledby='scan-map-title'
      PaperProps={{ sx: { borderRadius: '16px', m: '16px' } }}
    >
      {grid && side && (
        <Box sx={{ p: { xs: '16px', sm: '22px' } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              mb: '12px',
            }}
          >
            <Typography
              id='scan-map-title'
              sx={{ fontSize: '16px', fontWeight: 700, color: HEADING_FG }}
            >
              {comparison.keyword}
            </Typography>
            <Box
              component='button'
              type='button'
              onClick={close}
              aria-label='Close'
              sx={{
                border: 0,
                bgcolor: 'transparent',
                color: MUTED_FG,
                fontSize: '22px',
                lineHeight: 1,
                cursor: 'pointer',
                p: '4px',
              }}
            >
              ×
            </Box>
          </Box>
          {/* A classic baseline stored without its points has none to
              enlarge. */}
          {canEnlarge(comparison.baseline) && (
            <Box
              role='group'
              aria-label='Scan'
              sx={{ display: 'flex', gap: '6px', mb: '12px' }}
            >
              <Box
                component='button'
                type='button'
                aria-pressed={side === 'baseline'}
                onClick={() => choose('baseline')}
                sx={tabSx(side === 'baseline')}
              >
                Baseline
              </Box>
              <Box
                component='button'
                type='button'
                aria-pressed={side === 'now'}
                onClick={() => choose('now')}
                sx={tabSx(side === 'now')}
              >
                Now
              </Box>
            </Box>
          )}
          <Box sx={{ width: 'min(100%, 52vh)', mx: 'auto' }}>
            <ScanGridSquare
              grid={grid}
              map={side === 'baseline' ? baselineMap : nowMap}
              onMapFailed={onMapFailed}
              selectedCell={cell}
              onSelectCell={setCell}
              pinScale={1.6}
            />
          </Box>
          <Box sx={{ mt: '14px', minHeight: 92 }}>
            <PointDetail grid={grid} index={cell} />
          </Box>
        </Box>
      )}
    </Dialog>
  );
}

/** Baseline and now, side by side with an arrow between. Fluid columns and a
 * square aspect ratio, so the pair fills the design's 860px and shrinks with
 * the page instead of scrolling sideways on a phone. */
function GridPair({
  comparison,
  baselineDate,
  baselineMap,
  nowMap,
  onMapFailed,
  onOpen,
  note,
}: {
  comparison: ReportScanComparison;
  baselineDate?: string | null;
  baselineMap: ScanMap | null;
  nowMap: ScanMap | null;
  onMapFailed: (src: string) => void;
  onOpen: (side: ScanMapSide) => void;
  /** Why the two squares differ, when the scan area changed. */
  note?: string;
}) {
  const { baseline, now } = comparison;
  const baselineLabel = formatBaselineDate(baselineDate);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: { xs: '12px', sm: '28px' },
        alignItems: 'start',
        maxWidth: 860,
        mx: 'auto',
      }}
    >
      <Box>
        <ScanGridSquare
          grid={baseline}
          fallbackDimension={now.dimension}
          map={baselineMap}
          onMapFailed={onMapFailed}
          onOpen={canEnlarge(baseline) ? () => onOpen('baseline') : undefined}
          openLabel='Enlarge the baseline scan'
        />
        <Typography sx={{ mt: '10px', fontSize: '13px', color: MUTED_FG }}>
          {baseline
            ? `Baseline ${baselineLabel ? `${baselineLabel} · ` : ''}${percentText(baseline.visibilityRate)}%`
            : 'Baseline · no earlier scan'}
        </Typography>
      </Box>
      <Typography
        aria-hidden
        sx={{ fontSize: '20px', color: FAINT_FG, alignSelf: 'center' }}
      >
        →
      </Typography>
      <Box>
        <ScanGridSquare
          grid={now}
          fallbackDimension={now.dimension}
          map={nowMap}
          onMapFailed={onMapFailed}
          onOpen={() => onOpen('now')}
          openLabel='Enlarge the latest scan'
        />
        <Typography
          sx={{
            mt: '10px',
            fontSize: '13px',
            color: HEADING_FG,
            fontWeight: 600,
          }}
        >
          Now · {percentText(now.visibilityRate)}%
        </Typography>
      </Box>
      {note && (
        <Typography
          role='note'
          sx={{
            gridColumn: '1 / -1',
            fontSize: '13px',
            color: MUTED_FG,
            textAlign: 'center',
          }}
        >
          {note}
        </Typography>
      )}
    </Box>
  );
}

/** A street map the square should draw, by its image URL. */
type ScanMap = { src: string };

const sameView = (a?: ReportScanMapView | null, b?: ReportScanMapView | null) =>
  Boolean(
    a &&
      b &&
      a.lat === b.lat &&
      a.lng === b.lng &&
      a.zoom === b.zoom &&
      a.size === b.size,
  );

/**
 * The two notes a comparison can carry, decided together so the Top 3 /
 * Top 10 caveat is said exactly once.
 *
 * When the scan area changed, the note sits under the maps and carries the
 * caveat with it; otherwise a resized grid's caveat sits under the stat
 * cards. "Both maps are drawn at the same scale" is only said while the one
 * shared map is actually drawn on both sides: with separate maps, or the
 * stand-in, the squares each fill their frame, and saying otherwise would be
 * the misreading the note exists to prevent.
 */
function scanNotes(
  comparison: ReportScanComparison,
  sameScale: boolean,
): { underMaps?: string; underStats?: string } {
  const { baseline, now } = comparison;
  // The same gate the stat cards use: a baseline that never ranked has no
  // Top 3 / Top 10 to compare against in the first place.
  const comparable = baseline != null && baseline.averageRank !== null;
  const caveat = 'so Top 3 and Top 10 are not directly comparable';
  const grid = (dimension: number) => `${dimension}x${dimension}`;
  const pointsText = (points: number) =>
    `${points} ${points === 1 ? 'point' : 'points'}`;
  // What changed between the two scans, when the point count did. Told
  // apart because each reads wrongly as another: a 15x15 square that became
  // a 15x15 circle did not change size, and a classic scan had no grid size
  // to change from.
  let change: string | null = null;
  if (baseline && comparison.dimensionsMatch === false) {
    if (baseline.dimension === 0 && now.dimension === 0) {
      // Both classic: the service radius moved, adding or dropping a ring.
      const counts =
        baseline.points != null && now.points != null
          ? `, from ${baseline.points} to ${pointsText(now.points)}`
          : '';
      change = `Your classic scan's area changed since the baseline${counts}`;
    } else if (baseline.dimension === 0 && now.dimension > 0) {
      change = `Your scan changed from the classic layout${baseline.points ? ` (${pointsText(baseline.points)})` : ''} to a ${grid(now.dimension)} grid since the baseline`;
    } else if (now.dimension === 0) {
      change = 'Your scan changed to the classic layout since the baseline';
    } else if (baseline.dimension !== now.dimension) {
      change = `Your scan grid changed from ${grid(baseline.dimension)} to ${grid(now.dimension)} since the baseline`;
    } else if (
      comparison.layoutMatches === true &&
      baseline.points != null &&
      now.points != null &&
      baseline.points !== now.points
    ) {
      // Same size and layout: some points didn't respond in one of the scans,
      // either one, so the sentence doesn't say which reached fewer.
      change = `This scan reached ${pointsText(now.points)} and the baseline reached ${baseline.points}`;
    } else {
      // Same size, so the layout moved: square to circle or back. Snapshots
      // from before `points` was stored cannot say by how much.
      const counts =
        baseline.points != null && now.points != null
          ? `, from ${baseline.points} to ${pointsText(now.points)}`
          : '';
      change = `Your scan grid changed shape since the baseline${counts}`;
    }
  }

  if (comparison.scanAreaChanged && baseline) {
    const scale = sameScale ? ' Both maps are drawn at the same scale.' : '';
    // A shape or size change moves the area with it, and the caveat goes
    // where that is said — never dropped because the area changed too.
    if (change) {
      return {
        underMaps: `${change}${comparable ? `, ${caveat}` : ''}.${scale}`,
      };
    }
    return { underMaps: `Your scan area changed since the baseline.${scale}` };
  }
  if (comparable && change && baseline) {
    // A resize with no area flag is an older snapshot, and keeps the
    // sentence those reports were sent with.
    const resized =
      baseline.dimension > 0 && baseline.dimension !== now.dimension;
    return {
      underStats: resized
        ? `The scan grid size changed since the baseline scan, ${caveat}.`
        : `${change}, ${caveat}.`,
    };
  }
  return {};
}

/** A signed delta where positive is always an improvement — true for every
 * one of the four stat cards (average rank's `change` is pre-inverted by the
 * backend; visibility/top3/top10 are naturally "higher is better"). Getting
 * this backwards would show every gain as a loss. Only a gain is drawn: the
 * client report shows improvements only, so a loss or a flat week leaves
 * the figure on its own. */
function DeltaChip({
  delta,
  decimals = 0,
  suffix = '',
}: {
  delta: number | null;
  decimals?: number;
  suffix?: string;
}) {
  if (delta === null) return null;
  // These are averages over many scan points, so exact `=== 0` is
  // vanishingly rare — a 0.01 wobble is scan noise, not a real move, and
  // would otherwise flash a stray up/down arrow every week. The threshold is
  // tighter for a one-decimal value (average rank) than a whole-number one
  // (visibility/top3/top10 points), since a whole-number delta has no
  // sub-1 noise to absorb in the first place.
  if (delta < (decimals > 0 ? 0.05 : 0.5)) return null;
  return (
    <Box
      component='span'
      sx={{ fontSize: '13px', fontWeight: 700, color: POSITIVE_FG }}
    >
      {`↑ ${delta.toFixed(decimals)}${suffix}`}
    </Box>
  );
}

/** The circled "i" beside a stat's label. A real tooltip rather than a
 * `title` attribute: these sentences are the only place the page explains
 * what a scan point is, and `title` is invisible to touch and to keyboards. */
function StatInfo({ tip }: { tip: string }) {
  return (
    <Tooltip
      title={tip}
      enterTouchDelay={0}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: TOOLTIP_BG,
            color: ON_DARK_FG,
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: 1.5,
            p: '10px 12px',
            borderRadius: '10px',
            maxWidth: 260,
            boxShadow: '0 10px 30px -10px rgba(15,15,26,0.6)',
          },
        },
      }}
    >
      <Box
        component='span'
        tabIndex={0}
        aria-label={tip}
        className='report-no-print'
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: FAINT_FG,
          cursor: 'help',
          '&:hover, &:focus-visible': { color: WORK_POST_FG },
        }}
      >
        <Box
          component='svg'
          viewBox='0 0 24 24'
          width={15}
          height={15}
          fill='none'
          stroke='currentColor'
          strokeWidth={2}
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden
        >
          <circle cx='12' cy='12' r='10' />
          <path d='M12 16v-4' />
          <path d='M12 8h.01' />
        </Box>
      </Box>
    </Tooltip>
  );
}

/** Average rank, visibility rate, top 3 and top 10 — each against baseline.
 * All four deltas are null (rendered with no chip) when the keyword has no
 * usable baseline, rather than comparing "now" against nothing. */
function StatCards({
  comparison,
  caveat,
}: {
  comparison: ReportScanComparison;
  /** Set by `scanNotes`, which owns when it is said and where. */
  caveat?: string;
}) {
  const { baseline, now, change } = comparison;
  // A baseline OBJECT existing is not the same as a baseline WORTH comparing
  // against: a keyword that never ranked at any point in its earlier scan has
  // `averageRank: null` but real zeros for visibility/top3/top10, and
  // diffing "now" against those zeros reads as a gain out of a measurement
  // that never happened. All four cards gate on the same condition `change`
  // already effectively does, so none of the other three can disagree with it.
  const hasBaseline = baseline != null && baseline.averageRank !== null;
  // The owner can resize the scan grid at any time. Top-3/Top-10 are raw
  // point counts, so comparing them across a size change compares 9 points
  // to 81 — `averageRank` is a mean and stays comparable regardless, which is
  // why only these two gate on it too. Absent (an older snapshot) reads as
  // comparable, matching the behaviour before this flag existed.
  const dimensionsMatch = comparison.dimensionsMatch !== false;
  // The real number of points in THIS keyword's scan, so every sentence
  // below counts the grid the reader is looking at rather than the design
  // mockup's 25.
  //
  // `points` rather than `cells.length`: a circle scan is DRAWN on the square
  // that encloses it, so the cell count is about a quarter larger than the
  // scan — "average rank across 225 points" for a scan that visited 177.
  // Falls back for a snapshot taken before the field existed, where the two
  // were the same number anyway for the square scans of the time.
  const points = now.points ?? now.cells.length;

  const cards = [
    {
      label: `Average rank across ${points} points`,
      tip: `We scan ${points} points across your service area. This is your average map pack position across all of them, so it reflects the whole area rather than one spot.`,
      value: now.averageRank === null ? '20+' : now.averageRank.toFixed(1),
      delta: hasBaseline ? change : null,
      decimals: 1,
      suffix: '',
    },
    {
      label: 'Visibility rate',
      tip: `The share of those ${points} scan points where you appear in the top ${now.visibilityTop ?? 10}. Higher means more of your service area can find you.`,
      value: `${percentText(now.visibilityRate)}%`,
      // One decimal, as the two rates beside it are printed.
      delta: hasBaseline ? now.visibilityRate - baseline.visibilityRate : null,
      decimals: 1,
      suffix: ' pts',
    },
    {
      label: 'Top 3',
      tip: `How many of the ${points} points rank you in the top 3 — the three businesses Google shows before "More places".`,
      value: String(now.top3),
      delta: hasBaseline && dimensionsMatch ? now.top3 - baseline.top3 : null,
      decimals: 0,
      suffix: '',
    },
    {
      label: 'Top 10',
      tip: `How many of the ${points} points rank you in the top 10, meaning you appear once someone expands the map results.`,
      value: String(now.top10),
      delta: hasBaseline && dimensionsMatch ? now.top10 - baseline.top10 : null,
      decimals: 0,
      suffix: '',
    },
  ];

  return (
    <Box sx={{ mt: '24px', pt: '22px', borderTop: `1px solid ${INNER_RULE}` }}>
      <Box
        sx={{
          display: 'grid',
          // Exactly two columns, as the design pairs them — not auto-fit,
          // which breaks the four into one row of thin cards on a wide page.
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
          },
          gap: '14px',
        }}
      >
        {cards.map(card => (
          <Box
            key={card.label}
            sx={{
              border: '1px solid',
              borderColor: INNER_RULE,
              borderRadius: '14px',
              padding: '16px 18px',
              bgcolor: STAT_CARD_BG,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                mb: '6px',
              }}
            >
              <Typography
                component='span'
                sx={{ fontSize: '13px', color: MUTED_FG }}
              >
                {card.label}
              </Typography>
              <StatInfo tip={card.tip} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                component='span'
                sx={{
                  fontSize: '30px',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                {card.value}
              </Typography>
              <DeltaChip
                delta={card.delta}
                decimals={card.decimals}
                suffix={card.suffix}
              />
            </Box>
          </Box>
        ))}
      </Box>
      {caveat && (
        <Typography sx={{ fontSize: '12px', color: MUTED_FG, mt: '12px' }}>
          {caveat}
        </Typography>
      )}
    </Box>
  );
}

/** "All keywords" plus one chip per tracked keyword. `report-no-print`: this
 * is a control for reading the page, not part of the document a client would
 * print or save as a PDF. */
function KeywordChips({
  keywords,
  selected,
  onSelect,
}: {
  keywords: ReportScanComparison[];
  selected: number | null;
  onSelect: (index: number | null) => void;
}) {
  const chipSx = (active: boolean) => ({
    border: '1px solid',
    borderColor: active ? WORK_POST_FG : CARD_BORDER,
    bgcolor: active ? WORK_POST_BG : ACCENT_PILL_BG,
    color: active ? WORK_POST_FG : BODY_FG,
    fontSize: '12px',
    fontWeight: active ? 700 : 600,
    borderRadius: '9999px',
    padding: '5px 11px',
    cursor: 'pointer',
    '&:hover': { borderColor: 'rgba(124,58,237,0.55)' },
  });
  return (
    <Box
      className='report-no-print'
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        margin: '8px 0 12px',
      }}
    >
      <Box
        component='button'
        type='button'
        onClick={() => onSelect(null)}
        sx={chipSx(selected === null)}
      >
        All keywords
      </Box>
      {keywords.map((row, index) => (
        <Box
          key={row.keyword}
          component='button'
          type='button'
          onClick={() => onSelect(index)}
          sx={chipSx(selected === index)}
        >
          {row.keyword}
        </Box>
      ))}
    </Box>
  );
}

/** The change column's pill — same convention as `DeltaChip` (positive is
 * always an improvement, and only an improvement is drawn) but boxed,
 * matching the design's table cell treatment. */
function ChangeBadge({ change }: { change: number | null }) {
  // `change` is always an average-rank difference (one decimal place), so
  // the same "not exactly zero, but noise" reasoning as `DeltaChip` applies
  // — the 0.05 threshold matches that chip's one-decimal case. A loss, a
  // flat week and a null change (no baseline to compare against) all leave
  // the cell empty.
  if (change === null || change < 0.05) return null;
  return (
    <Box
      component='span'
      data-testid='change-badge'
      sx={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '9999px',
        fontSize: '13px',
        fontWeight: 700,
        color: POSITIVE_FG,
        bgcolor: POSITIVE_BG,
      }}
    >
      {`↑ ${change.toFixed(1)}`}
    </Box>
  );
}

/** The design's own table metrics. Local rather than the shared `tableSx`:
 * this table has a selectable row, a left accent rail and far looser rows
 * than the compact tables elsewhere on the page. */
const scanTableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '14px',
  '& th': {
    padding: '8px',
    color: BODY_FG,
    fontWeight: 600,
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${CARD_BORDER}`,
    whiteSpace: 'nowrap',
  },
  '& th:first-of-type': {
    textAlign: 'left',
    width: '26%',
    padding: '8px 10px 8px 0',
  },
  '& th:last-of-type': { textAlign: 'right', padding: '8px 0 8px 8px' },
  '& td': {
    padding: '16px 8px',
    borderBottom: `1px solid ${INNER_RULE}`,
    textAlign: 'center',
    color: RANK_OTHER_FG,
    fontSize: '14px',
  },
  '& td:first-of-type': { textAlign: 'left', padding: '16px 10px' },
  '& td:last-of-type': { textAlign: 'right', padding: '16px 0 16px 8px' },
} as const;

/**
 * Columns a phone drops. Seven columns needed 620px, so on a phone only
 * Keyword and the baseline showed, and "Avg now" and "Change" — what the
 * reader is there for — sat off-screen behind a sideways scroll nobody knew
 * about. The stat cards above already carry visibility and the top-3/top-10
 * counts.
 */
const WIDE_ONLY = { display: { xs: 'none', sm: 'table-cell' } } as const;

/**
 * Change is the last column a phone shows, but `:last-of-type` still picks
 * the hidden Top 10 cell, so it gets the table's right-edge styling here.
 */
const LAST_ON_PHONE = {
  // `&&` doubles the class, so this outranks the table's `& td` / `& th`
  // rules (class + element) rather than losing to them as a plain cell
  // style did: the header moved right and every body cell stayed centred.
  '&&': {
    textAlign: { xs: 'right', sm: 'center' },
    pr: { xs: 0, sm: 1 },
  },
} as const;

/** The extended keyword table. Renders on its own (no `onSelect`) when there
 * is no grid to select into, e.g. every keyword has `dimension` 0. */
function ScanTable({
  keywords,
  selected,
  onSelect,
  baselineDate,
}: {
  keywords: ReportScanComparison[];
  selected: number | null;
  onSelect?: (index: number) => void;
  baselineDate?: string | null;
}) {
  // Each keyword's baseline can come from a different week. One date in the
  // header is only true when every row shares it; otherwise each row says
  // its own, under its average.
  const rowLabel = (row: ReportScanComparison) =>
    row.baseline == null
      ? null
      : formatBaselineDate(row.baselineDate ?? baselineDate);
  const labels = new Set(keywords.map(rowLabel).filter(Boolean));
  const sharedLabel = labels.size === 1 ? Array.from(labels)[0] : null;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table sx={{ ...scanTableSx, minWidth: { xs: 0, sm: 620 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Keyword</TableCell>
            <TableCell sx={WIDE_ONLY}>
              Avg baseline{sharedLabel ? ` ${sharedLabel}` : ''}
            </TableCell>
            {/* The one column the reader is here for, so the design darkens
                and bolds its heading alone. */}
            <TableCell sx={{ color: HEADING_FG, fontWeight: 700 }}>
              Avg now
            </TableCell>
            <TableCell sx={LAST_ON_PHONE}>Change</TableCell>
            <TableCell sx={WIDE_ONLY}>Visibility</TableCell>
            <TableCell sx={WIDE_ONLY}>Top 3</TableCell>
            <TableCell sx={WIDE_ONLY}>Top 10</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {keywords.map((row, index) => {
            const active = selected === index;
            const band = averageBand(row.now.averageRank);
            return (
              <TableRow
                key={row.keyword}
                onClick={onSelect ? () => onSelect(index) : undefined}
                sx={{
                  cursor: onSelect ? 'pointer' : 'default',
                  bgcolor: active ? ROW_SELECTED_BG : 'transparent',
                  ...(onSelect ? { '&:hover': { bgcolor: ROW_HOVER_BG } } : {}),
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: active ? ROW_SELECTED_FG : HEADING_FG,
                    // The rail that marks which row's grid is drawn above.
                    // Always 3px, transparent when idle, so selecting a row
                    // never shifts the column by three pixels.
                    borderLeft: `3px solid ${active ? WORK_RULE_STRONG : 'transparent'}`,
                    borderRadius: '6px 0 0 6px',
                  }}
                >
                  {row.keyword}
                </TableCell>
                <TableCell sx={{ ...WIDE_ONLY, color: MUTED_FG }}>
                  {row.baseline == null
                    ? '—'
                    : averageText(row.baseline.averageRank)}
                  {labels.size > 1 && rowLabel(row) && (
                    <Box
                      component='span'
                      sx={{
                        display: 'block',
                        fontSize: '11px',
                        color: FAINT_FG,
                      }}
                    >
                      {rowLabel(row)}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Box
                    component='span'
                    data-testid='avg-now'
                    sx={{
                      display: 'inline-block',
                      minWidth: 30,
                      padding: '4px 10px',
                      borderRadius: '8px',
                      fontWeight: 700,
                      color: band.fg,
                      bgcolor: band.bg,
                    }}
                  >
                    {averageText(row.now.averageRank)}
                  </Box>
                </TableCell>
                <TableCell sx={LAST_ON_PHONE}>
                  <ChangeBadge change={row.change} />
                </TableCell>
                <TableCell sx={{ ...WIDE_ONLY, fontWeight: 600 }}>
                  {percentText(row.now.visibilityRate)}%
                </TableCell>
                <TableCell sx={WIDE_ONLY}>{row.now.top3}</TableCell>
                <TableCell sx={WIDE_ONLY}>{row.now.top10}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * The whole scan-grid block: chips, the baseline/now grid pair, four stat
 * cards, and the extended table — `MapPackSection` renders this only when
 * `mapPack.scanGrid` has rows (see `ReportSections.tsx`).
 */
export function ScanGridSection({
  keywords,
  baselineDate,
  scanArea,
  agencyView = false,
  mapUrl,
}: {
  keywords: ReportScanComparison[];
  baselineDate?: string | null;
  scanArea?: ReportScanArea | null;
  /** The street map for a grid, by its position in `keywords`. Absent where
   * the page has no token to fetch one with, which keeps the stand-in. */
  mapUrl?: ScanMapUrl;
  /** The agency footnote below the table is hidden from the client's own
   * view: when the next scan runs is the agency's operational detail, not
   * part of the report the business is reading. */
  agencyView?: boolean;
}) {
  // Which keyword's grid is drawn. `null` means "All keywords", which
  // defaults to the strongest performer below — a SELECTION among real
  // per-keyword grids, never an averaged/fabricated one.
  const [selected, setSelected] = useState<number | null>(null);
  const [enlarged, setEnlarged] = useState<ScanMapSide | null>(null);
  // Image URLs that would not load. Keyed by URL, which is unique per keyword
  // and side, so a failure on one keyword's map never blanks another's.
  const [failedMaps, setFailedMaps] = useState<string[]>([]);
  const onMapFailed = (src: string) =>
    setFailedMaps(held => (held.includes(src) ? held : [...held, src]));

  if (keywords.length === 0) return null;

  const bestIndex = keywords.reduce((best, row, index) => {
    const bestRank = keywords[best].now.averageRank ?? Infinity;
    const rank = row.now.averageRank ?? Infinity;
    return rank < bestRank ? index : best;
  }, 0);
  const activeIndex = selected ?? bestIndex;
  const active = keywords[activeIndex];

  // One map for both sides when the backend drew them on one — the same
  // view on both — loaded once, from the `now` URL, rather than paid for
  // twice. Separate views (the scans too far apart to share, or nothing
  // changed) each load their own.
  // Only when the area changed: two unchanged scans can fit to the same view
  // by coincidence, and each should still load, and fail, on its own.
  const shared =
    Boolean(active.scanAreaChanged) &&
    sameView(active.baseline?.map, active.now.map);
  const mapFor = (
    grid: ReportScanGrid | null,
    side: ScanMapSide,
  ): ScanMap | null => {
    if (!grid?.map || !mapUrl) return null;
    const src = mapUrl(activeIndex, shared ? 'now' : side);
    return failedMaps.includes(src) ? null : { src };
  };
  const baselineMap = mapFor(active.baseline, 'baseline');
  const nowMap = mapFor(active.now, 'now');
  const notes = scanNotes(active, shared && !!baselineMap && !!nowMap);

  // Some scan configurations never populate the geographic grid (dimension
  // 0) even though the keyword is still tracked and averaged. When that is
  // true for every tracked keyword there is nothing to draw, so the
  // chips/grids/stat cards are skipped entirely and the table — which needs
  // no `dimension` to show real averages — carries the section alone.
  const hasGrid = keywords.some(row => canEnlarge(row.now));
  // `points`, for the same reason the stat cards use it: a circle scan is
  // drawn on the square that encloses it, so the cell count runs about a
  // quarter over the scan. Reading the cells here had this sentence saying
  // 225 scan points directly beneath cards that correctly said 177.
  const points = active.now.points ?? active.now.cells.length;

  const nextScan = formatWeekdayMonthDay(scanArea?.nextScanDate ?? null);
  const scanNote = agencyView
    ? [
        nextScan ? `Next scan ${nextScan}` : '',
        `${keywords.length} tracked keyword${keywords.length === 1 ? '' : 's'}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Box>
      {hasGrid && (
        <Box sx={{ mb: '28px' }}>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: BODY_FG,
              mb: '4px',
            }}
          >
            Scan grid
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            {/* "All keywords" alone would read, in a screenshot cropped to
                just the heading and grid, as a combined view of every
                keyword — it is really one keyword's real grid. The caption
                under the grids names it; the heading stays the design's. */}
            {selected === null ? 'All keywords' : active.keyword}
          </Typography>
          <KeywordChips
            keywords={keywords}
            selected={selected}
            onSelect={setSelected}
          />
          <GridPair
            comparison={active}
            // The keyword's own baseline; older snapshots only kept the
            // section's earliest.
            baselineDate={active.baselineDate ?? baselineDate}
            baselineMap={baselineMap}
            nowMap={nowMap}
            onMapFailed={onMapFailed}
            onOpen={setEnlarged}
            note={notes.underMaps}
          />
          <EnlargedScanMap
            comparison={active}
            side={enlarged}
            onSide={setEnlarged}
            onClose={() => setEnlarged(null)}
            baselineMap={baselineMap}
            nowMap={nowMap}
            onMapFailed={onMapFailed}
          />
          <Typography
            sx={{
              fontSize: '12px',
              color: FAINT_FG,
              textAlign: 'center',
              mt: '12px',
            }}
          >
            {selected === null
              ? `Showing your strongest keyword, ${active.keyword}. Pick another keyword above to scope this panel to it.`
              : `Showing ${active.keyword}. Choose All keywords to return to your strongest.`}
            {/* A control for reading the page, so not part of a print. */}
            <span className='report-no-print'>
              {' '}
              Tap a map to enlarge it and see who ranks at each point.
            </span>
          </Typography>
          <StatCards comparison={active} caveat={notes.underStats} />
        </Box>
      )}
      <Box
        sx={
          hasGrid
            ? { pt: '22px', borderTop: `1px solid ${INNER_RULE}` }
            : undefined
        }
      >
        <Typography
          sx={{
            fontSize: '13px',
            color: MUTED_FG,
            lineHeight: 1.65,
            mb: '14px',
          }}
        >
          Each number is your average position across the{' '}
          {hasGrid ? `${points} scan points` : 'scanned points'} for that search
          — 1 is the top result and lower is better.
          {hasGrid && ' Click a keyword to see its scan grid above.'}
        </Typography>
        <ScanTable
          keywords={keywords}
          // `selected`, not `activeIndex`. "All keywords" picks the strongest
          // keyword to DRAW above, but it is not a row the reader chose, and
          // marking it leaves the table permanently showing a selection
          // nobody made — which is also not what the design draws. The rail
          // and tint appear once a keyword is actually picked.
          selected={hasGrid ? selected : null}
          onSelect={hasGrid ? setSelected : undefined}
          baselineDate={baselineDate}
        />
        {scanNote && (
          <Typography
            sx={{
              mt: '14px',
              pt: '12px',
              fontSize: '12px',
              color: MUTED_FG,
              borderTop: `1px dashed ${CARD_BORDER}`,
            }}
          >
            {scanNote}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
